import React, { useEffect, useState } from "react";
import { ShieldCheck, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const fmt = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

export function MfaEnrollmentWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const res = await api.get("/admin/mfa-enrollment"); setData(res.data); }
    catch { /* admin-gated; ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return null;
  const allEnrolled = data.total > 0 && data.pending === 0;

  return (
    <Card className="mb-6 p-6" data-testid="admin-mfa-enrollment-widget">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-[640] text-foreground">
            <ShieldCheck className="h-5 w-5 text-primary" /> Admin MFA enrollment
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Two-factor readiness for admin &amp; moderator accounts — confirm everyone is enrolled before enforcing MFA.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data.mfa_enforced
            ? <Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50" data-testid="admin-mfa-enforced-badge">Enforcement ON</Badge>
            : <Badge variant="secondary" data-testid="admin-mfa-enforced-badge">Enforcement OFF</Badge>}
          <Button type="button" variant="ghost" size="sm" onClick={load} aria-label="Refresh" data-testid="admin-mfa-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-x-10 gap-y-2 text-sm" data-testid="admin-mfa-summary">
        <div>
          <span className="text-muted-foreground">Enrolled</span>
          <div className="text-xl font-[680] text-foreground" data-testid="admin-mfa-enrolled-count">{data.enrolled}/{data.total}</div>
        </div>
        <div>
          <span className="text-muted-foreground">Pending</span>
          <div className={`text-xl font-[680] ${data.pending > 0 ? "text-amber-600" : "text-teal-600"}`} data-testid="admin-mfa-pending-count">{data.pending}</div>
        </div>
      </div>
      {allEnrolled
        ? <p className="mt-2 text-sm text-teal-700" data-testid="admin-mfa-all-enrolled">All admin-capable accounts have MFA enabled — safe to enforce.</p>
        : <p className="mt-2 text-sm text-amber-700" data-testid="admin-mfa-pending-note">{data.pending} account(s) still need to enable MFA before enforcement.</p>}

      <div className="mt-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>MFA</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead>Last verified</TableHead>
              <TableHead>Backup codes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.accounts.map((a) => (
              <TableRow key={a.user_id} data-testid={`admin-mfa-row-${a.user_id}`}>
                <TableCell className="font-[560] text-foreground">{a.name}</TableCell>
                <TableCell className="text-muted-foreground">{a.email}</TableCell>
                <TableCell className="capitalize">{a.role}</TableCell>
                <TableCell>
                  {a.mfa_enabled
                    ? <Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50" data-testid={`admin-mfa-status-${a.user_id}`}>Enabled</Badge>
                    : <Badge variant="secondary" className="text-amber-700" data-testid={`admin-mfa-status-${a.user_id}`}>Not enabled</Badge>}
                </TableCell>
                <TableCell className="text-muted-foreground">{fmt(a.mfa_enabled_at)}</TableCell>
                <TableCell className="text-muted-foreground">{fmt(a.mfa_last_verified_at)}</TableCell>
                <TableCell className="text-muted-foreground">{a.mfa_enabled ? a.backup_codes_remaining : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
